import { apiCustomer, apiCustomerPublic } from "@/api"
import type {
  Allocation,
  AppliedCoupon,
  CartLinePayload,
  CouponEvaluation,
  EvaluateCouponsRequest,
  LoyaltyReward,
  LoyaltyRewardsResponse,
  RejectedCoupon,
  RulePromotionEvaluation,
  Violation,
} from "./types"

type Json = Record<string, unknown>

function isObject(value: unknown): value is Json {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function list(value: unknown): Json[] {
  return Array.isArray(value) ? value.filter(isObject) : []
}

function str(value: unknown): string {
  return typeof value === "string" ? value : ""
}

function num(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0
}

function allocationOf(raw: Json): Allocation {
  const target = str(raw.target)
  return {
    couponCode: str(raw.couponCode),
    ruleId: str(raw.ruleId),
    target:
      target === "ALLOCATION_TARGET_LINE" ||
      target === "ALLOCATION_TARGET_OPTION" ||
      target === "ALLOCATION_TARGET_CART" ||
      target === "ALLOCATION_TARGET_DELIVERY_FEE"
        ? target
        : "ALLOCATION_TARGET_UNSPECIFIED",
    lineId: str(raw.lineId),
    optionLineId: str(raw.optionLineId),
    amountCents: num(raw.amountCents),
  }
}

function violationOf(raw: Json): Violation {
  const ref = isObject(raw.ref) ? raw.ref : null
  const kind = str(ref?.kind)
  return {
    reason: str(raw.reason),
    ruleId: str(raw.ruleId),
    ref: ref
      ? {
          kind:
            kind === "REF_KIND_ITEM" || kind === "REF_KIND_CATEGORY" || kind === "REF_KIND_OPTION"
              ? kind
              : "REF_KIND_UNSPECIFIED",
          id: str(ref.id),
          externalCode: str(ref.externalCode),
          name: str(ref.name),
          scopeItemId: str(ref.scopeItemId),
          scopeItemExternalCode: str(ref.scopeItemExternalCode),
        }
      : undefined,
    lineId: str(raw.lineId),
    requiredQuantity: num(raw.requiredQuantity),
    actualQuantity: num(raw.actualQuantity),
  }
}

function appliedOf(raw: Json): AppliedCoupon {
  const couponClass = str(raw.class)
  return {
    code: str(raw.code),
    couponId: str(raw.couponId),
    ruleId: str(raw.ruleId),
    class:
      couponClass === "COUPON_CLASS_ITEM" || couponClass === "COUPON_CLASS_ORDER" || couponClass === "COUPON_CLASS_SHIPPING"
        ? couponClass
        : "COUPON_CLASS_UNSPECIFIED",
    discountCents: num(raw.discountCents),
  }
}

function rejectedOf(raw: Json): RejectedCoupon {
  return { code: str(raw.code), reason: str(raw.reason), violations: list(raw.violations).map(violationOf) }
}

/** Reads a `coupons/evaluate` body defensively: missing lists are empty, unknown enums are UNSPECIFIED. */
export function parseCouponEvaluation(data: unknown): CouponEvaluation {
  const raw = isObject(data) ? data : {}
  return {
    applied: list(raw.applied).map(appliedOf),
    rejected: list(raw.rejected).map(rejectedOf),
    allocations: list(raw.allocations).map(allocationOf),
    totalDiscountCents: num(raw.totalDiscountCents),
    maxCouponsPerOrder: Math.max(1, num(raw.maxCouponsPerOrder)),
  }
}

/** Reads a `rule-promotions/evaluate` body defensively. */
export function parseRulePromotionEvaluation(data: unknown): RulePromotionEvaluation {
  const raw = isObject(data) ? data : {}
  return {
    applied: list(raw.applied).map((applied) => ({
      promotionId: str(applied.promotionId),
      promotionName: str(applied.promotionName),
      ruleId: str(applied.ruleId),
      discountCents: num(applied.discountCents),
    })),
    allocations: list(raw.allocations).map(allocationOf),
  }
}

function rewardOf(raw: Json): LoyaltyReward {
  const item = isObject(raw.item) ? raw.item : {}
  const availability = isObject(raw.availability) ? raw.availability : null
  return {
    id: str(raw.id),
    merchantId: str(raw.merchantId),
    item: { itemId: str(item.itemId), externalCode: str(item.externalCode), name: str(item.name) },
    pointsCost: num(raw.pointsCost),
    maxPerOrder: num(raw.maxPerOrder),
    active: raw.active === true,
    availability: availability
      ? {
          redeemOnlyWithinAvailability: availability.redeemOnlyWithinAvailability === true,
          blocks: list(availability.blocks).map((block) => ({
            id: str(block.id),
            days: Array.isArray(block.days) ? block.days.filter((day): day is string => typeof day === "string") : [],
            schedules: list(block.schedules).map((shift) => ({
              id: str(shift.id),
              startTime: str(shift.startTime),
              endTime: str(shift.endTime),
            })),
          })),
        }
      : undefined,
    version: num(raw.version),
  }
}

/** Reads a `rewards` body defensively; rewards without an id or a cost are dropped. */
export function parseLoyaltyRewards(data: unknown): LoyaltyRewardsResponse {
  const raw = isObject(data) ? data : {}
  return {
    rewards: list(raw.rewards)
      .map(rewardOf)
      .filter((reward) => reward.id && reward.active && reward.pointsCost > 0),
    balance: num(raw.balance),
  }
}

/** POST /merchants/:merchant_id/coupons/evaluate (customer from the session). */
export async function evaluateCoupons(
  merchantId: string,
  body: EvaluateCouponsRequest,
  signal?: AbortSignal,
): Promise<CouponEvaluation> {
  const { data } = await apiCustomer.post<unknown>(`/merchants/${merchantId}/coupons/evaluate`, body, { signal })
  return parseCouponEvaluation(data)
}

/** Most codes and lines the public preview takes in one call (customer-service-gateway limits). */
export const PUBLIC_EVALUATION_LIMITS = { codes: 5, lines: 50 } as const

/** POST /public/merchants/:merchant_id/coupons/evaluate: the same verdict for a visitor without a session (customer-bound rules are rechecked at checkout). */
export async function evaluateCouponsPublic(
  merchantId: string,
  body: Pick<EvaluateCouponsRequest, "couponCodes" | "lines" | "deliveryFeeCents">,
  signal?: AbortSignal,
): Promise<CouponEvaluation> {
  const { data } = await apiCustomerPublic.post<unknown>(`/public/merchants/${merchantId}/coupons/evaluate`, body, { signal })
  return parseCouponEvaluation(data)
}

/** POST /loyalties/by-merchant/:merchant_id/rule-promotions/evaluate (customer from the session). */
export async function evaluateRulePromotions(
  merchantId: string,
  body: { lines: CartLinePayload[]; deliveryFeeCents: number },
  signal?: AbortSignal,
): Promise<RulePromotionEvaluation> {
  const { data } = await apiCustomer.post<unknown>(
    `/loyalties/by-merchant/${merchantId}/rule-promotions/evaluate`,
    body,
    { signal },
  )
  return parseRulePromotionEvaluation(data)
}

/** GET /loyalties/by-merchant/:merchant_id/rewards: redeemable items plus the customer's balance. */
export async function fetchLoyaltyRewards(merchantId: string): Promise<LoyaltyRewardsResponse> {
  const { data } = await apiCustomer.get<unknown>(`/loyalties/by-merchant/${merchantId}/rewards`)
  return parseLoyaltyRewards(data)
}
